"use client";

import { useState, useEffect, useRef } from "react";
import { ChevronDown } from "lucide-react";

type NavigationItem = {
  key: string;
  label: string;
};

type ArticleNavigationProps = {
  items: NavigationItem[];
  title: string;
};

const ArticleNavigation = ({ items, title }: ArticleNavigationProps) => {
  const [activeKey, setActiveKey] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  // Refs for scroll spy and manual scroll lock
  const isManualScrolling = useRef(false);
  const scrollTimeout = useRef<NodeJS.Timeout | null>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const containerRef = useRef<HTMLElement>(null);

  // Scroll Spy Observer
  useEffect(() => {
    const navHeight = window.innerWidth < 768 ? 68 : 80;
    const headerOffset = navHeight + 20; // Offset for navbar + some padding

    const observer = new IntersectionObserver(
      (entries) => {
        if (isManualScrolling.current) return;

        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveKey(entry.target.id);
          }
        });
      },
      {
        // Adjust rootMargin to trigger when element is near the top
        rootMargin: `-${headerOffset}px 0px -70% 0px`,
        threshold: 0,
      },
    );

    items.forEach((item) => {
      const element = document.getElementById(item.key);
      if (element) observer.observe(element);
    });

    return () => observer.disconnect();
  }, [items]);

  // Scroll active menu item into view (Vertical)
  useEffect(() => {
    const scrollActiveItemToView = () => {
      const container = containerRef.current;
      const activeItem = document.getElementById(`nav-item-${activeKey}`);

      if (container && activeItem) {
        const containerRect = container.getBoundingClientRect();
        const itemRect = activeItem.getBoundingClientRect();

        // Calculate scroll position to center the item
        const scrollTop =
          container.scrollTop +
          itemRect.top -
          containerRect.top -
          containerRect.height / 2 +
          itemRect.height / 2;

        container.scrollTo({
          top: scrollTop,
          behavior: "smooth",
        });
      }
    };

    scrollActiveItemToView();
  }, [activeKey]);

  const handleLinkClick = (e: React.MouseEvent, key: string) => {
    e.preventDefault();
    isManualScrolling.current = true;
    if (scrollTimeout.current) clearTimeout(scrollTimeout.current);

    setActiveKey(key);

    // Smooth scroll to section
    const element = document.getElementById(key);
    if (element) {
      const navHeight = window.innerWidth < 768 ? 68 : 80;
      const offset = navHeight + 20;

      const elementPosition =
        element.getBoundingClientRect().top + window.scrollY;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });

      // Reset manual scrolling lock after animation
      scrollTimeout.current = setTimeout(() => {
        isManualScrolling.current = false;
      }, 1000);
    }
  };

  if (items.length === 0) return null;

  return (
    <section
      ref={containerRef}
      className="bg-white rounded-lg py-4 w-full card-shadow block"
    >
      <div
        className="flex px-5 items-center justify-between cursor-pointer lg:cursor-default mb-2 lg:mb-6"
        onClick={() => {
          if (window.innerWidth < 1024) setIsOpen(!isOpen);
        }}
      >
        <h3 className="text-primary title m-0">{title}</h3>
        <ChevronDown
          size={24}
          className={`lg:hidden text-primary transition-transform duration-300 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </div>

      <div
        className={`grid transition-[grid-template-rows] duration-300 ease-in-out lg:grid-rows-[1fr] ${
          isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        }`}
      >
        <div className="overflow-hidden">
          <ul
            ref={listRef}
            className="flex flex-col gap-2 relative mt-2 lg:mt-0 overflow-y-auto max-h-[60vh] px-5"
          >
            {items.map((item, i) => (
              <li
                key={item.key}
                id={`nav-item-${item.key}`}
                onClick={(e) => {
                  handleLinkClick(e, item.key);
                  if (window.innerWidth < 1024) {
                    setIsOpen(false);
                  }
                }}
                className={`cursor-pointer hover:underline py-1 ${
                  activeKey === item.key
                    ? "text-primary underline"
                    : "text-text-secondary hover:text-primary"
                }`}
              >
                {item.label}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
};

export default ArticleNavigation;
